import { PrismicButton } from '@components/PrismicButton';
import { PrismicError } from '@components/PrismicError';
import { PrismicRichText } from '@components/PrismicRichText';
import { PrismicTitle } from '@components/PrismicTitle';
import WidgetTrustpilot from '@components/WidgetTrustpilot/WidgetTrustpilot';
import { useMediaQuery } from '@monofront/business/hooks';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage/ResponsiveImage';
import { HeroHookSlice } from '@monofront/slice-machine/prismicio-types';
import { isFilled } from '@prismicio/client';
import { colors } from '@monofront/theme';

type HeroHookProps = {
  slice: HeroHookSlice;
};

export const HeroHook = ({ slice }: HeroHookProps) => {
  const { primary } = slice;

  const backgroundColor = primary.background_color ?? colors.yellow100;
  const hasCta =
    isFilled.link(primary.primary_cta) || isFilled.link(primary.secondary_cta);

  return (
    <>
      <div
        className="tw-relative tw-flex tw-w-full tw-justify-around"
        style={{ backgroundColor }}
      >
        <div className="layout-aligned tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-6 desktop:tw-flex-row desktop:tw-justify-between">
          <div className="tw-shrink tw-px-[--layout-margin] tw-pt-[--space-8] tw-text-center desktop:tw-max-w-[590px] desktop:tw-py-[--space-8] desktop:tw-pl-0 desktop:tw-text-left">
            <PrismicTitle
              field={primary.title}
              titleStyleField={primary.title_style}
              className="tw-mb-3"
            />
            <PrismicRichText field={primary.content} />
            {hasCta && (
              <div className="tw-mt-6 tw-flex tw-flex-col tw-gap-2 desktop:tw-flex-row desktop:tw-place-content-start">
                {isFilled.link(primary.primary_cta) && (
                  <PrismicButton field={primary.primary_cta} />
                )}
                {isFilled.link(primary.secondary_cta) && (
                  <PrismicButton field={primary.secondary_cta} />
                )}
              </div>
            )}
            {primary.caption && (
              <p className="tw-lf-caption-regular tw-mt-2">{primary.caption}</p>
            )}
            <OptionalContent slice={slice} />
          </div>
          {isFilled.image(primary.main_image) && (
            <div className="tw-relative tw-aspect-[3/2] tw-w-full desktop:tw-max-w-[620px]">
              <ResponsiveImage
                src={primary.main_image.url}
                alt={primary.main_image.alt ?? undefined}
                layout="fill"
                objectFit="scale-down"
                fetchPriority="high"
              />
            </div>
          )}
        </div>
      </div>
      <style jsx>
        {`
          .layout-aligned {
            padding-left: calc(
              calc(clamp(0px, 100vw, 1920px) - var(--layout-max-width)) / 2
            );
          }

          @media (min-width: 801px) and (max-width: 1248px) {
            .layout-aligned {
              margin-left: var(--layout-margin);
            }
          }
        `}
      </style>
    </>
  );
};

type OptionalContentProps = {
  slice: HeroHookSlice;
};

const OptionalContent = ({ slice }: OptionalContentProps) => {
  const isMobile = useMediaQuery(800);

  switch (slice.variation) {
    case 'default':
      return null;

    case 'withSmallImage':
      if (!isFilled.image(slice.primary.small_image)) {
        return <PrismicError />;
      }

      return (
        <div className="tw-relative tw-mt-10 tw-h-24">
          <ResponsiveImage
            src={slice.primary.small_image.url}
            layout="fill"
            objectFit="contain"
            objectPosition={isMobile ? 'center' : 'left'}
          />
        </div>
      );

    case 'withTrustpilotWidget':
      if (!isFilled.keyText(slice.primary.trustpilot_widget_id)) {
        return <PrismicError />;
      }

      return (
        <div className="tw-my-10">
          <WidgetTrustpilot
            templateId={slice.primary.trustpilot_widget_id}
            height="96px"
            width="100%"
            theme="light"
          />
        </div>
      );
  }
};
